// src/pages/championships/RoundStats.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import roundStatsService from '../../services/roundStatsService';
import championshipService from '../../services/championshipService'; 
import { FaFutbol, FaRegIdCard, FaSortNumericUp, FaMedal } from 'react-icons/fa';
import { GiGoalKeeper, GiSoccerKick } from "react-icons/gi";
import { RiAlertFill } from "react-icons/ri";
import './RoundStats.css';

// Componente helper para a medalha
const Medal = ({ rank }) => {
  const medalColors = {
    0: '#FFD700', // Gold
    1: '#C0C0C0', // Silver
    2: '#CD7F32'  // Bronze
  };
  
  if (rank in medalColors) {
    return <FaMedal style={{ color: medalColors[rank], marginRight: '8px' }} />;
  }
  return null;
};

// Função auxiliar para obter URL da imagem do time
const getTeamLogoUrl = (badge) => {
  if (!badge) return '/default-team-badge.png';
  if (badge.startsWith('http')) return badge;
  const cloudinaryBaseUrl = 'https://res.cloudinary.com/seu-cloud-name/image/upload/v1/badges/'; // Substitua 'seu-cloud-name'
  return cloudinaryBaseUrl + badge;
};

function RoundStats({ championshipId: propChampionshipId }) {
  const { id: urlId, championshipId: legacyUrlId } = useParams();
  const championshipId = propChampionshipId || urlId || legacyUrlId;

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [selectedType, setSelectedType] = useState('round'); 
  const [selectedValue, setSelectedValue] = useState('1');   
  const [availableRounds, setAvailableRounds] = useState([]); 
  const [availablePhases, setAvailablePhases] = useState([]); 
  const [championshipDetails, setChampionshipDetails] = useState(null); 

  const phaseDisplayNames = useMemo(() => ({
    'group': 'Fase de Grupos', 
    'roundOf16': 'Oitavas de Final',
    'quarterFinals': 'Quartas de Final',
    'semiFinals': 'Semifinais',
    'thirdPlace': 'Disputa de 3º Lugar',
    'final': 'Final'
  }), []);

  useEffect(() => {
    const fetchChampionshipAndPhases = async () => {
      if (!championshipId) {
          setError("ID do campeonato não disponível. Verifique a URL ou a prop.");
          return;
      }
      try {
        const champRes = await championshipService.getById(championshipId);
        const details = champRes.data || champRes;
        setChampionshipDetails(details);

        const maxRounds = details.groups?.length ? Math.max(...details.groups.map(g => (g.matches || []).length / (g.teams.length / 2 || 1))) : 10;
        setAvailableRounds(Array.from({ length: maxRounds > 0 ? maxRounds : 1 }, (_, i) => String(i + 1)));

        const knockoutPhasesForFormat = [];
        const format = details.format;

        if (format === 'Fase de grupos + Mata-mata (32 times)' || format === 'Mata-mata (16 times)') {
            knockoutPhasesForFormat.push('roundOf16', 'quarterFinals', 'semiFinals', 'final', 'thirdPlace');
        } 
        else if (format === 'Fase de grupos + Mata-mata (16 times)' || format === 'Mata-mata (8 times)') {
            knockoutPhasesForFormat.push('quarterFinals', 'semiFinals', 'final', 'thirdPlace');
        } 
        else if (format === 'Formato Personalizado') {
            knockoutPhasesForFormat.push('roundOf16', 'quarterFinals', 'semiFinals', 'final', 'thirdPlace'); 
        }

        setAvailablePhases(knockoutPhasesForFormat);
        
        if (selectedType === 'round' && availableRounds.length > 0) {
            setSelectedValue(availableRounds[0]);
        } else if (selectedType === 'phase' && knockoutPhasesForFormat.length > 0) {
            setSelectedValue(knockoutPhasesForFormat[0]);
        }

      } catch (err) {
        console.error('Erro ao carregar detalhes do campeonato:', err);
        setError("Erro ao carregar detalhes do campeonato para configurar filtros.");
      }
    };
    
    if (championshipId) {
      fetchChampionshipAndPhases();
    }
  }, [championshipId]); 

  const fetchStats = useCallback(async (type, value) => {
    if (!type || !value || !championshipId) {
      setError('ID do campeonato, tipo ou valor da fase/rodada ausente.');
      return;
    }
    setLoading(true);
    setError('');
    setStats(null);
    try {
      const data = await roundStatsService.getRoundStats(championshipId, type, value);
      
      if (data && data.artilheiros) {
        data.sortedScorers = Object.entries(data.artilheiros)
          .sort(([, a], [, b]) => b - a);
      }
      setStats(data);
    } catch (err) {
      console.error('Erro ao buscar dados da rodada/fase:', err);
      setError(err.message || 'Erro ao buscar estatísticas. Verifique se a rodada/fase existe e está concluída.');
    } finally {
      setLoading(false);
    }
  }, [championshipId]);

  useEffect(() => {
    if (championshipId && selectedType && selectedValue) {
      fetchStats(selectedType, selectedValue);
    }
  }, [championshipId, selectedType, selectedValue, fetchStats]);

  const handleFetchClick = (e) => {
    e.preventDefault();
    fetchStats(selectedType, selectedValue);
  };

  const formatMatchDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Data inválida';
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) + 
           ' às ' + 
           date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };
  
  const getStatusText = (status) => {
    switch (status) {
        case 'scheduled': return 'Agendada';
        case 'in_progress': return 'Em Andamento';
        case 'postponed': return 'Adiada';
        case 'canceled': return 'Cancelada';
        default: return 'Desconhecido';
    }
  };

  return (
    <div className="round-stats-container">
      <form className="round-stats-controls" onSubmit={handleFetchClick}>
        <label htmlFor="typeSelector">
          <FaSortNumericUp />
          <span>Ver estatísticas de:</span>
        </label>
        <select id="typeSelector" value={selectedType} onChange={(e) => {setSelectedType(e.target.value); setSelectedValue(e.target.value === 'round' ? availableRounds[0] || '1' : availablePhases[0] || '');}} disabled={loading}>
          <option value="round">Rodada</option>
          {availablePhases.length > 0 && <option value="phase">Fase de Mata-Mata</option>}
        </select>

        {selectedType === 'round' && (
          <input
            type="number"
            value={selectedValue}
            onChange={(e) => setSelectedValue(e.target.value)}
            placeholder="Nº da Rodada"
            min="1"
            max={availableRounds.length > 0 ? availableRounds[availableRounds.length - 1] : "1"}
            disabled={loading || availableRounds.length === 0}
          />
        )}

        {selectedType === 'phase' && (
          <select value={selectedValue} onChange={(e) => setSelectedValue(e.target.value)} disabled={loading || availablePhases.length === 0}>
            {availablePhases.length === 0 && <option value="">Nenhuma fase disponível</option>}
            {availablePhases.map(phase => (
              <option key={phase} value={phase}>{phaseDisplayNames[phase] || phase}</option>
            ))}
          </select>
        )}

        <button type="submit" disabled={loading || !selectedValue}>
          {loading ? 'Buscando...' : 'Buscar'}
        </button>
      </form>

      {loading && <p className="round-stats-loading">Carregando estatísticas...</p>}
      
      {error && !loading && (
        <p className="round-stats-error">
          <RiAlertFill /> {error}
        </p>
      )}

      {stats && (
        <>
          {/* Seção de partidas pendentes/agendadas */}
          {stats.pendingMatches && stats.pendingMatches.length > 0 && (
            <div className="round-stats-section pending-matches-section">
              <div className="section-header">
                <FaFutbol />
                <h3>Partidas Agendadas / Em Andamento</h3>
              </div>
              <ul>
                {stats.pendingMatches.map((m, i) => (
                  <li key={m._id || i} className="match-item pending-match-item">
                    <img src={getTeamLogoUrl(m.teamA?.badge)} alt={m.teamA?.name} style={{ width: '24px', height: '24px', borderRadius: '50%' }} />
                    <span>{m.teamA?.name}</span>
                    <span className="pending-score">vs</span>
                    <span>{m.teamB?.name}</span>
                    <img src={getTeamLogoUrl(m.teamB?.badge)} alt={m.teamB?.name} style={{ width: '24px', height: '24px', borderRadius: '50%' }} />
                    <small>{formatMatchDate(m.date)}</small>
                    <span className={`match-status-badge status-${m.status}`}>{getStatusText(m.status)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* O restante das estatísticas (apenas de jogos concluídos) */}
          {stats.jogos && stats.jogos.length > 0 ? ( 
            <div className="stats-grid">
              {/* Card de Jogos (Concluídos) */}
              <div className="round-stats-section">
                <div className="section-header">
                  <FaFutbol />
                  <h3>Jogos Concluídos da {stats.identifier}</h3>
                </div>
                <ul>
                  {stats.jogos.map((j, i) => (
                    <li key={i} className="match-item">
                      <img src={getTeamLogoUrl(j.homeBadge)} alt={j.home} style={{ width: '24px', height: '24px', borderRadius: '50%' }} />
                      <span>{j.home}</span>
                      <strong className="score">{j.score}</strong>
                      <span>{j.away}</span>
                      <img src={getTeamLogoUrl(j.awayBadge)} alt={j.away} style={{ width: '24px', height: '24px', borderRadius: '50%' }} />
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Card de Artilheiros com RANKING */}
              <div className="round-stats-section">
                <div className="section-header">
                  <GiSoccerKick />
                  <h3>Artilheiros</h3>
                </div>
                <ul>
                  {stats.sortedScorers && stats.sortedScorers.length > 0 ? stats.sortedScorers.map(([nome, gols], index) => (
                    <li key={index}>
                      <span className="player-rank">
                        <Medal rank={index} />
                        {nome}
                      </span>
                      <strong>{gols} gol(s)</strong>
                    </li>
                  )) : <li>Nenhum gol marcado.</li>}
                </ul>
              </div>

              {/* Card de Goleiros com RANKING */}
              <div className="round-stats-section">
                <div className="section-header">
                  <GiGoalKeeper />
                  <h3>Goleiros Menos Vazados</h3>
                </div>
                <ul>
                  {stats.melhoresGoleiros && stats.melhoresGoleiros.length > 0 ? stats.melhoresGoleiros.map((g, index) => (
                    <li key={index}>
                      <span className="player-rank">
                        <Medal rank={index} />
                        {g.name} <small>({g.team})</small>
                      </span>
                    <strong>{g.goalsConceded} sofrido(s)</strong>
                    </li>
                  )) : <li>Nenhuma partida concluída para calcular estatísticas de goleiros.</li>}
                </ul>
              </div>

              {/* Card de Disciplina MODIFICADO */}
              <div className="round-stats-section">
                <div className="section-header">
                  <FaRegIdCard />
                  <h3>Disciplina</h3>
                </div>

                {(!stats.cartões?.amarelos || stats.cartões.amarelos.length === 0) &&
                 (!stats.cartões?.vermelhos || stats.cartões.vermelhos.length === 0) ? (
                  <ul>
                    <li>Nenhum cartão aplicado nesta rodada/fase.</li>
                  </ul>
                ) : (
                  <div className="discipline-sub-sections">
                    <div className="discipline-sub-section">
                        <h4 className="sub-section-title yellow">
                            <span className="card-icon">🟨</span> Cartões Amarelos
                        </h4>
                        <ul>
                            {stats.cartões?.amarelos && stats.cartões.amarelos.length > 0 ? (
                                stats.cartões.amarelos.map((j, i) => (
                                    <li key={`yellow-${i}`}>
                                        <span>{j}</span>
                                    </li>
                                ))
                            ) : (
                                <li>Nenhum.</li>
                            )}
                        </ul>
                    </div>

                    <div className="discipline-sub-section">
                        <h4 className="sub-section-title red">
                             <span className="card-icon">🟥</span> Cartões Vermelhos
                        </h4>
                        <ul>
                            {stats.cartões?.vermelhos && stats.cartões.vermelhos.length > 0 ? (
                                stats.cartões.vermelhos.map((j, i) => (
                                    <li key={`red-${i}`}>
                                        <span>{j}</span>
                                    </li>
                                ))
                            ) : (
                                <li>Nenhum.</li>
                            )}
                        </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : ( 
            stats.pendingMatches && stats.pendingMatches.length > 0 ? (
                <p className="round-stats-error">
                    <RiAlertFill /> Não há partidas concluídas para esta {stats?.identifier || 'seleção'} para gerar estatísticas detalhadas.
                    Veja as partidas agendadas acima.
                </p>
            ) : (
                <p className="round-stats-error">
                    <RiAlertFill /> Nenhuma partida encontrada para esta {stats?.identifier || 'seleção'}.
                </p>
            )
          )}
        </>
      )}
    </div>
  );
}

export default RoundStats;